import { Component, Input, OnInit } from '@angular/core';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { SpinnerService } from 'src/app/utils/services/spinner.service';
import {StudentShoppingCartService} from "../services/student-shopping-cart.service";
import {FilterDefinition} from "../../model/CourseFilters";
import { NativeServiceApiProvider } from '../../services/native-api-provider.service';
import { Page, Pageable } from '../../utils/pageable';
import * as LessonsV3 from '../model/v3/lessons';
import { ProductsUtilsV3 } from './v3/products-utils';

@Component({
    template: '',
    standalone: false
})
export abstract class CoreProductsBase implements OnInit {
  @Input()
  set currency(currency: string) {
    this._currency = currency;
    this.refreshProducts();
  }

  _currency: string;
  _studentId: number
  _chosenCourse: FilterDefinition
  @Input()
  set chosenCourse(lang: FilterDefinition) {
    this._chosenCourse = lang
  }
  get chosenCourse() {
    return this._chosenCourse
  }
  @Input()
  set studentId(value: number) {
    this._studentId = value;
  }
  get studentId() {
    return this._studentId;
  }
  lights: LessonsV3.ProductVariantPreview[] = [];
  std: LessonsV3.ProductVariantPreview[] = [];
  intensive: LessonsV3.ProductVariantPreview[] = [];
  pag: LessonsV3.ProductVariantPreview[] = [];
  progressPlans: LessonsV3.ProductVariantPreview[] = [];
  books: LessonsV3.ProductVariantPreview[] = [];

  protected constructor(
    private apiProvider: NativeServiceApiProvider,
    private spinner: SpinnerService,
    private shoppingCart: StudentShoppingCartService
  ) {}

  ngOnInit(): void {}

  abstract getEbookCategoryLabel(): string;
  abstract getPaygCategoryLabel(): string;
  abstract getIntensiveCategoryLabel(): string;
  abstract getStdCategoryLabel(): string;
  abstract getLightCategoryLabel(): string;
  abstract getProductsLabel(): string;

  filterProducts(products: LessonsV3.ProductVariantPreview[]): void {
    const englishProducts = ProductsUtilsV3.filterByProductLabel(
      products,
      this.getProductsLabel()
    );
    this.lights = ProductsUtilsV3.orderByOrderLabel(
      ProductsUtilsV3.filterByVariantLabel(
        englishProducts,
        this.getLightCategoryLabel()
      )
    );
    this.std = ProductsUtilsV3.orderByOrderLabel(
      ProductsUtilsV3.filterByVariantLabel(
        englishProducts,
        this.getStdCategoryLabel()
      )
    );
    this.intensive = ProductsUtilsV3.orderByOrderLabel(
      ProductsUtilsV3.filterByVariantLabel(
        englishProducts,
        this.getIntensiveCategoryLabel()
      )
    );
    this.pag = ProductsUtilsV3.orderByOrderLabel(
      ProductsUtilsV3.filterByVariantLabel(
        englishProducts,
        this.getPaygCategoryLabel()
      )
    );
    this.pag = this.filterByCodeSuffixes(this.pag, ['pag1', 'pag2', 'pag3']);
    this.progressPlans = [
      this.pickByCodeSuffix(this.lights, 'light4w'),
      this.pickByCodeSuffix(this.std, 'std4w'),
      this.pickByCodeSuffix(this.intensive, 'int4w'),
    ].filter(Boolean) as LessonsV3.ProductVariantPreview[];

    this.books = ProductsUtilsV3.orderByOrderLabel(
      ProductsUtilsV3.filterByProductLabel(products, this.getEbookCategoryLabel())
    );
  }

  getImage(product: LessonsV3.ProductVariantPreview) {
    const imageLabel = product?.product?.labels?.find((l) =>
      l.startsWith('image:')
    );
    if (!imageLabel) {
      return '';
    }
    return imageLabel.slice('image:'.length);
  }

  addItem(product: LessonsV3.ProductVariantPreview) {
    this.shoppingCart.addItem(product);
  }

  private pickByCodeSuffix(
    products: LessonsV3.ProductVariantPreview[],
    suffix: string
  ): LessonsV3.ProductVariantPreview | undefined {
    if (!products) {
      return undefined;
    }
    return products.find((product) => product?.code?.endsWith(suffix));
  }

  private filterByCodeSuffixes(
    products: LessonsV3.ProductVariantPreview[],
    suffixes: string[]
  ): LessonsV3.ProductVariantPreview[] {
    if (!products) {
      return [];
    }
    return products.filter((product) =>
      suffixes.some((suffix) => product?.code?.endsWith(suffix))
    );
  }

  refreshProducts(): any {
    if (!this._currency) {return; }
    this.spinner
      .trace<LessonsV3.ProductVariantPreview[]>(
        this.loadProducts().pipe(tap((products) => this.filterProducts(products)))
      )
      .subscribe();
  }

  protected loadProducts(): Observable<LessonsV3.ProductVariantPreview[]> {
    if (!this._currency) {
      return of([]);
    }
    return this.apiProvider.colV3().pipe(
      switchMap((api) =>
        api.getProductVariants(
          null,
          'snipcart',
          null,
          'en',
          this._currency,
          ['regular'],
          new Pageable(0, 500, null)
        )
      ),
      map((page: Page<LessonsV3.ProductVariantPreview>) =>
        (page?.content ?? []).filter(
          (variant) =>
            !!variant?.code &&
            !!variant?.product?.code &&
            (variant.price?.value ?? 0) > 0
        )
      )
    );
  }
}
